import * as React from 'react';
import { Link } from 'react-router';
import { __, __r } from 'react-i18n';
import {
  FlexRow,
  FlexColCenter,
  FlexCol,
  FlexRowCenterHorizontal,
  Flexbox,
} from '../../Defaults/Flexbox/Flexbox';
import styled, { withTheme } from 'styled-components';
import { rem } from 'polished';
import { IconType, SvgIcon } from '../General/SvgIcon';
import { TextP } from '../../Defaults/Text';
import { InfoText, InfoType } from '../General/InfoText';
import { ZemplinLoaderWrapper } from '../../..';
import { getFinishedOrderInfo } from '../../../utilities/selectors';
import { ButtonLink } from '../General/Button';
import { ItemsProductTable } from './Items/ItemsProductTable';
import { ItemsTabRow } from './Items/FinishedOrderItemsTabRow';
import { TableWrapper } from '../General/TableWrapper';
import { TableRow } from '../General/Table';

interface Props {
  theme: any;
  order: any;
  isFetching: boolean;
}

function FinishedOrder({ theme, order, isFetching }: Props) {
  const { orderNr } = getFinishedOrderInfo(order);

  const delivery = order.delivery.delivery_typename;
  const payment = order.payment.payment_typename;

  const onlyItems =
    order.items && order.items.filter(item => item.is_meta_item !== true);
  const productsComponent =
    onlyItems &&
    onlyItems.map((p: any, i) => {
      return (
        <StyledTableRow
          key={p.product_id}
          hasBottomBorder={i === order.items.length - 1 ? false : true}
        >
          <ItemsTabRow product={p} />
        </StyledTableRow>
      );
    });

  return (
    <Wrapper>
      {isFetching ? (
        <ZemplinLoaderWrapper />
      ) : (
        <>
          <LeftWrapper>
            <Title>{__('Ďakujeme za váš nákup!')}</Title>
            <OrderNr>Objednávka č. {orderNr}</OrderNr>
            <SmallText>
              {__(
                'Na vašu emailovú adresu bolo zaslané potvrdenie o prijatí objednávky. \n Stav môžete sledovať v detaile objednávky vo vašom účte.',
              )}
            </SmallText>
            <BackOrderButton to={'/'}>
              {__('Prejsť na hlavnú stránku')}
            </BackOrderButton>
          </LeftWrapper>
          <RightWrapper>
            <OrderItemsTableWrapper
              noItemsText={__('Nenašli sa žiadne produkty')}
              rows={productsComponent}
            />
            <DeliveryInfoWrapper>
              <DeliveryPartWrapper>
                <SvgIcon
                  width={16}
                  height={16}
                  viewBox={'0 0 16 16'}
                  icon={IconType.check}
                  fill={'black'}
                  cursor={'pointer'}
                  marginRight={8}
                />
                <DeliveryPaymentText>{delivery}</DeliveryPaymentText>
              </DeliveryPartWrapper>

              <DeliveryPartWrapper>
                <SvgIcon
                  width={16}
                  height={16}
                  viewBox={'0 0 16 16'}
                  icon={IconType.check}
                  fill={'black'}
                  cursor={'pointer'}
                  marginRight={8}
                />
                <DeliveryPaymentText>{payment}</DeliveryPaymentText>
              </DeliveryPartWrapper>
            </DeliveryInfoWrapper>
          </RightWrapper>
        </>
      )}
    </Wrapper>
  );
}

const DeliveryInfoWrapper = styled(Flexbox)`
  margin-left: ${rem(8)};
  justify-content: center;
  flex-direction: row;

  ${({ theme }) => theme.mediab.m810`
    flex-direction: column;
  `}
`;

const DeliveryPartWrapper = styled.div`
  margin-right: ${rem(32)};
  display: inline-block;

  ${({ theme }) => theme.mediab.m810`
    margin-right: 0;
  `}
`;

const OrderItemsTableWrapper = styled(TableWrapper)`
  margin-bottom: ${rem(48)};
`;

const Wrapper = styled(FlexRow)`
  width: 100%;
  margin: 0 auto;
  padding: ${rem(80)} 0;
  max-width: ${rem(1180)};
  justify-content: center;

  ${({ theme }) => theme.mediab.m810`
    flex-flow: column;
    align-items: center;
    padding: ${rem(32)} 0 ${rem(56)};
  `}
`;

const LeftWrapper = styled(FlexCol)`
  min-width: 50%;
  padding: ${rem(24)} 0;
  margin: 0;
  height: fit-content;
  max-width: ${rem(440)};
  justify-content: flex-start;

  ${({ theme }) => theme.mediab.m810`
    padding: 0;
    margin-right: 0;
    align-items: center;
  `}
`;

const RightWrapper = styled(LeftWrapper)`
  padding: ${rem(32)};
  align-items: flex-start;
  margin-right: 0;
  min-width: 50%;

  ${({ theme }) => theme.mediab.m810`
    border: 0;
    padding: 0;
    margin-top: ${rem(32)};
  `}
`;

const Title = styled.h2`
  margin-bottom: ${rem(32)};
  margin-top: 0;
  font-size: ${rem(30)};
  font-family: ${({ theme }) => theme.font.secondary};
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: ${rem(39)};
  font-weight: 500;
  text-transform: uppercase;

  ${({ theme }) => theme.mediab.m810`
    font-size: ${rem(24)};
    line-height: ${rem(31)};
    text-align: center;
  `}
`;

const SmallText = styled(TextP)`
  font-weight: 300;
  max-width: ${rem(440)};
  font-family: ${({ theme }) => theme.font.primary};
  line-height: ${rem(21)};
  font-size: ${rem(14)};
  margin-bottom: ${rem(32)};
  color: ${({ theme }) => theme.colors.textPrimary};

  ${({ theme }) => theme.mediab.m810`
    text-align: center;
  `}
`;

const DeliveryPaymentText = styled(TextP)`
  display: inline-block;
  font-weight: 300;
  font-family: ${({ theme }) => theme.font.primary};
  line-height: ${rem(21)};
  font-size: ${rem(14)};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const OrderNr = styled(SmallText)`
  font-weight: 400;
  font-family: ${({ theme }) => theme.font.primary};
  font-size: ${rem(14)};
  line-height: ${rem(21)};

  ${({ theme }) => theme.mediab.m810`
    text-align: center;
  `}
`;

const BackOrderButton = styled(ButtonLink)`
  max-width: ${rem(270)};
  font-size: ${rem(16)};
  font-family: ${({ theme }) => theme.font.primary};
  font-weight: 500;

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.primaryOnHover};
    outline: none;
  }
`;

const StyledTableRow = styled(TableRow)<{ hasBottomBorder: boolean }>`
  padding: ${rem(24)} 0;
  border-bottom: ${({ hasBottomBorder, theme }) =>
    hasBottomBorder ? theme.borders.primary : 'none'};
`;

const WithTheme: any = withTheme(FinishedOrder);
export { WithTheme as FinishedOrder };
